/**
 * Standard fetch logic for public external APIs (like CountriesNow)
 * These are kept separate from the internal secure apiServices to avoid 
 * encryption and base URL conflicts.
 */

export const getCountries = async () => {
    try {
        const response = await fetch("https://countriesnow.space/api/v0.1/countries/positions");
        const result = await response.json();
        if (!result.error) {
            return result.data.map(c => ({ label: c.name, value: c.name }));
        }
        return [];
    } catch (err) {
        console.error("Error fetching countries:", err);
        return [];
    }
};

export const getStatesByCountry = async (countryName) => {
    if (!countryName) return [];
    try {
        const response = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country: countryName })
        });
        const result = await response.json();
        if (!result.error) {
            return result.data.states.map(s => ({ label: s.name, value: s.name }));
        }
        return [];
    } catch (err) {
        console.error("Error fetching states:", err);
        return [];
    }
};

export const getCitiesByState = async (countryName, stateName) => {
    if (!countryName || !stateName) return [];
    try {
        const response = await fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country: countryName, state: stateName })
        });
        const result = await response.json();
        if (!result.error) {
            return result.data.map(city => ({ label: city, value: city }));
        }
        return [];
    } catch (err) {
        console.error("Error fetching cities:", err);
        return [];
    }
};

export const fetchLocationByPinCode = async (pinCode) => {
    const API_KEY = "3b071e72e12e4f0ba611b818041c2d0f"; // Ensure this matches exactly
    // Basic validation
    if (!pinCode || pinCode.length < 3) {
        return { success: false, error: "Invalid postal code" };
    }

    const isNumeric = /^\d+$/.test(pinCode);
    
    // If it's a 6 digit indian pin code, try the free postal API first
    if (isNumeric && pinCode.length === 6) {
        try {
            const res = await fetch(`https://api.postalpincode.in/pincode/${pinCode}`);
            const data = await res.json();
            
            if (data && data[0] && data[0].Status === "Success" && data[0].PostOffice && data[0].PostOffice.length > 0) {
                // Map the response to our standard format
                const results = data[0].PostOffice.map(po => ({
                    postcode: pinCode,
                    city: po.Block || po.District || po.Name,
                    state: po.State,
                    country: "India",
                    formatted: `${po.Name}, ${po.District}, ${po.State}, India - ${pinCode}`
                }));
                return { success: true, results };
            }
        } catch (err) {
            console.error("Postal API failed, falling back to Geoapify", err);
            // Fall through to Geoapify if postal API fails
        }
    }

    // Try Geoapify
    try {
        const res = await fetch(`https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(pinCode)}&apiKey=${API_KEY}`);
        
        // Handle unauthorized or other errors gracefully
        if (!res.ok) {
            if (res.status === 401) {
                 return { success: false, error: "Geoapify API key is required for international postal codes." };
            }
            return { success: false, error: "Invalid postal code" };
        }
        
        const data = await res.json();

        if (data.features && data.features.length > 0) {
            const results = data.features.map(f => ({
                postcode: f.properties.postcode || pinCode,
                city: f.properties.city || f.properties.county || f.properties.state_district,
                state: f.properties.state,
                country: f.properties.country,
                formatted: f.properties.formatted
            })).filter(r => r.city || r.state || r.country); // Filter out empty results
            
            if (results.length > 0) {
                 return { success: true, results };
            }
        }
        return { success: false, error: "Invalid postal code" };
    } catch (err) {
        console.error("Geoapify Geocoding failed:", err);
        return { success: false, error: "Failed to fetch location details." };
    }
};
